import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { BuildingOfficeIcon, DocumentTextIcon, PencilIcon, BellIcon, ShieldCheckIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import PolicyTab from '../components/Settings/PolicyTab';
import NotificationsTab from '../components/Settings/NotificationsTab';
import SecurityTab from '../components/Settings/SecurityTab';
import WatermarkTab from '../components/Settings/WatermarkTab';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface Company {
  id: string;
  name: string;
  domain: string;
  description: string;
  website: string;
  logo_url: string;
  app_version: string;
  contact_person: string;
  contact_person_email: string;
  contact_email: string;
  address: string;
  contract_id: string;
  is_active: boolean;
  total_licenses?: number;
  available_licenses?: number;
  settings?: string | any;
  created_at: string;
  updated_at: string;
}

const Settings: React.FC = () => {
  const { t } = useTranslation('settings');
  usePageTitle(t('title'), t('subtitle'));
  const { user } = useAuth();
  const location = useLocation();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    domain: '',
    description: '',
    website: '',
    logo_url: '',
    app_version: '',
    contact_person: '',
    contact_person_email: '',
    contact_email: '',
    address: '',
    contract_id: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  
  // Active tab state - will be synced with localStorage via useEffect
  const [activeTab, setActiveTabState] = useState<'company' | 'policies' | 'watermark' | 'notifications' | 'security'>('company');
  
  // Subscription for company (license/device limits) - fetched when company tab is shown
  const [subscription, setSubscription] = useState<{
    subscription: { max_full_licenses?: number; max_allowed_devices_per_user: number; max_folders_per_device: number; licensed_until?: string; [key: string]: unknown };
    is_license_valid: boolean;
    available_full_licenses?: number;
  } | null>(null);
  const [usages, setUsages] = useState<import('../services/api').CompanyUsagesResponse | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Sync tab state with localStorage when on settings page
  const isCompanyTabAvailable = user && (user.role === 'admin' || user.role === 'sysadmin');
  useEffect(() => {
    const isSettingsPage = location.pathname === '/settings';
    if (isSettingsPage) {
      const saved = localStorage.getItem('settings_activeTab');
      if (saved && ['company', 'policies', 'watermark', 'notifications', 'security'].includes(saved)) {
        const tab = saved as 'company' | 'policies' | 'watermark' | 'notifications' | 'security';
        // If saved tab is company but user has no Company tab, default to policies
        setActiveTabState((tab === 'company' && !isCompanyTabAvailable) ? 'policies' : tab);
      } else {
        setActiveTabState(isCompanyTabAvailable ? 'company' : 'policies');
      }
    } else {
      setActiveTabState(isCompanyTabAvailable ? 'company' : 'policies');
    }
  }, [location.pathname, isCompanyTabAvailable]);
  
  // Wrapper to update both state and localStorage
  const setActiveTab = (tab: 'company' | 'policies' | 'watermark' | 'notifications' | 'security') => {
    setActiveTabState(tab);
    localStorage.setItem('settings_activeTab', tab);
  };


  const fetchCompanyInfo = useCallback(async () => {
    if (!user || !user.company_id) return;
    
    try {
      setLoading(true);
      const response = await apiService.get(`/companies/${user.company_id}`);
      const companyData = response.data;
      setCompany(companyData);
      
      // Initialize form data
      setCompanyFormData({
        name: companyData.name || '',
        domain: companyData.domain || '',
        description: companyData.description || '',
        website: companyData.website || '',
        logo_url: companyData.logo_url || '',
        app_version: companyData.app_version || 'latest',
        contact_person: companyData.contact_person || '',
        contact_person_email: companyData.contact_person_email || '',
        contact_email: companyData.contact_email || '',
        address: companyData.address || '',
        contract_id: companyData.contract_id || '',
      });
    } catch (err) {
      console.error('Failed to fetch company info:', err);
      toast.error(t('toast.companyLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    fetchCompanyInfo();
  }, [user, fetchCompanyInfo]);

  // Fetch subscription for company (license/device limits) when on Company tab and user has company
  const fetchSubscription = useCallback(async () => {
    if (!user?.company_id || (user.role !== 'admin' && user.role !== 'sysadmin')) return;
    try {
      setSubscriptionLoading(true);
      const [subData, usageData] = await Promise.all([
        apiService.getSubscriptionByCompany(user.company_id),
        apiService.getCompanyUsages(user.company_id),
      ]);
      setSubscription(subData);
      setUsages(usageData);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setSubscription(null);
      setUsages(null);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user?.company_id, user?.role]);

  useEffect(() => {
    if (activeTab === 'company' && user?.company_id) {
      fetchSubscription();
    }
  }, [activeTab, user?.company_id, fetchSubscription]);

  // Company update functions
  const handleSaveCompany = async () => {
    if (!user?.company_id || !company) return;
    
    try {
      setSavingCompany(true);
      
      // Prepare update payload; only sysadmin can update domain and contract_id
      const payload: Record<string, unknown> = {
        name: companyFormData.name,
        description: companyFormData.description,
        website: companyFormData.website,
        logo_url: companyFormData.logo_url,
        app_version: companyFormData.app_version,
        contact_person: companyFormData.contact_person,
        contact_person_email: companyFormData.contact_person_email,
        contact_email: companyFormData.contact_email,
        address: companyFormData.address,
      };
      if (user?.role === 'sysadmin') {
        payload.domain = companyFormData.domain;
        payload.contract_id = companyFormData.contract_id || null;
      }
      
      const response = await apiService.patch(`/companies/${user.company_id}`, payload);
      const updatedCompany = response.data;
      
      setCompany(updatedCompany);
      setEditingCompany(false);
      toast.success(t('toast.companyUpdated'));
    } catch (err: any) {
      console.error('Failed to update company:', err);
      toast.error(err.response?.data?.error || t('toast.companyUpdateFailed'));
    } finally {
      setSavingCompany(false);
    }
  };

  return (
    <div className="space-y-6">
   

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto">
            {user && (user.role === 'admin' || user.role === 'sysadmin') && (
              <button
                onClick={() => setActiveTab('company')}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'company'
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={t('tabs.company')}
              >
                <BuildingOfficeIcon className="w-5 h-5 md:w-4 md:h-4 inline md:mr-2" />
                <span className="hidden md:inline">{t('tabs.company')}</span>
              </button>
            )}
            <button
              onClick={() => setActiveTab('policies')}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'policies'
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={t('tabs.policies')}
            >
              <DocumentTextIcon className="w-5 h-5 md:w-4 md:h-4 inline md:mr-2" />
              <span className="hidden md:inline">{t('tabs.policies')}</span>
            </button>
            <button
              onClick={() => setActiveTab('watermark')}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'watermark'
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={t('tabs.watermark')}
            >
              <RectangleStackIcon className="w-5 h-5 md:w-4 md:h-4 inline md:mr-2" />
              <span className="hidden md:inline">{t('tabs.watermark')}</span>
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'notifications'
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={t('tabs.notifications')}
            >
              <BellIcon className="w-5 h-5 md:w-4 md:h-4 inline md:mr-2" />
              <span className="hidden md:inline">{t('tabs.notifications')}</span>
            </button>
            <button
              onClick={() => setActiveTab('security')}
                className={`py-2 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'security'
                    ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={t('tabs.security')}
            >
              <ShieldCheckIcon className="w-5 h-5 md:w-4 md:h-4 inline md:mr-2" />
              <span className="hidden md:inline">{t('tabs.security')}</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'company' && (
        <div className="space-y-6">
          {/* Company Info Section */}
          <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900 transition-colors">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                    {t('company.companyInfo')}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                    {t('company.companyInfoDescription')}
                  </p>
                </div>
                {company && !editingCompany && (
                  <button
                    onClick={() => setEditingCompany(true)}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-governate-500 dark:border-governate-600 text-governate-600 dark:text-governate-400 bg-white dark:bg-gray-900 hover:bg-governate-50 dark:hover:bg-governate-900/20 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4 inline mr-2" />
                    {t('company.edit')}
                  </button>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-5 sm:p-0">
              {loading ? (
                <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{t('company.loadingCompanyInfo')}</div>
              ) : company ? (
                <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-800">
                  {/* Basic Information - Editable */}
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.companyName')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="text"
                          value={companyFormData.name}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.name
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.domain')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany && user?.role === 'sysadmin' ? (
                        <input
                          type="text"
                          value={companyFormData.domain}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, domain: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.domain
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.description')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <textarea
                          value={companyFormData.description}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, description: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent resize-none"
                        />
                      ) : (
                        company.description || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.website')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="url"
                          value={companyFormData.website}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, website: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                          placeholder={t('company.placeholderWebsite')}
                        />
                      ) : (
                        company.website ? (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-governate-600 dark:text-governate-400 hover:text-governate-500">
                            {company.website}
                          </a>
                        ) : t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.logoUrl')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="url"
                          value={companyFormData.logo_url}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, logo_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                          placeholder={t('company.placeholderLogoUrl')}
                        />
                      ) : (
                        company.logo_url || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.appVersion')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <select
                          value={companyFormData.app_version}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, app_version: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        >
                          <option value="latest">{t('company.latest')}</option>
                          <option value="1.0.0">1.0.0</option>
                          <option value="0.9.0">0.9.0</option>
                        </select>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {company.app_version || 'latest'}
                        </span>
                      )}
                    </dd>
                  </div>
                  
                  {/* Contact Information - Editable */}
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-t border-gray-200 dark:border-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.contactPerson')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="text"
                          value={companyFormData.contact_person}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, contact_person: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.contact_person || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.contactPersonEmail')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="email"
                          value={companyFormData.contact_person_email}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, contact_person_email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.contact_person_email || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.contactEmail')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <input
                          type="email"
                          value={companyFormData.contact_email}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, contact_email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.contact_email || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.address')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany ? (
                        <textarea
                          value={companyFormData.address}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, address: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent resize-none"
                        />
                      ) : (
                        company.address || t('company.na')
                      )}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.contractId')}</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                      {editingCompany && user?.role === 'sysadmin' ? (
                        <input
                          type="text"
                          value={companyFormData.contract_id}
                          onChange={(e) => setCompanyFormData({ ...companyFormData, contract_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        />
                      ) : (
                        company.contract_id || t('company.na')
                      )}
                    </dd>
                  </div>
                  
                  {/* Subscription Information - from subscription API (read-only) */}
                  {(subscription !== null || subscriptionLoading) && (
                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-t border-gray-200 dark:border-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.subscriptionInformation')}</dt>
                      <dd className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:mt-0 sm:col-span-2">
                        <p className="text-xs text-gray-400 dark:text-gray-500">{t('company.subscriptionReadOnly')}</p>
                      </dd>
                    </div>
                  )}
                  {subscriptionLoading && (
                    <div className="py-4 sm:py-5 sm:px-6 text-sm text-gray-500 dark:text-gray-400">{t('company.loadingCompanyInfo')}</div>
                  )}
                  {!subscriptionLoading && subscription && subscription.subscription && (
                    <>
                      {/* License counts — full, reader, auditor */}
                      {usages && (
                        <div className="py-4 sm:py-5 sm:px-6 col-span-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                <th className="pb-2 font-medium">{t('company.licenseType')}</th>
                                <th className="pb-2 font-medium text-right">{t('company.licenseMax')}</th>
                                <th className="pb-2 font-medium text-right">{t('company.licenseUsed')}</th>
                                <th className="pb-2 font-medium text-right">{t('company.licenseAvailable')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                              <tr>
                                <td className="py-2 text-gray-700 dark:text-gray-300">{t('company.licenseTypeFull')}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.full_licenses_max ?? '—'}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.full_licenses_used}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{Math.max(0, usages.full_licenses_max - usages.full_licenses_used)}</td>
                              </tr>
                              <tr>
                                <td className="py-2 text-gray-700 dark:text-gray-300">{t('company.licenseTypeReader')}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.reader_licenses_max ?? '—'}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.reader_licenses_used}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{Math.max(0, usages.reader_licenses_max - usages.reader_licenses_used)}</td>
                              </tr>
                              <tr>
                                <td className="py-2 text-gray-700 dark:text-gray-300">{t('company.licenseTypeAuditor')}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.auditor_licenses_max != null ? usages.auditor_licenses_max : t('company.licenseUnlimited')}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">{usages.auditor_licenses_used ?? 0}</td>
                                <td className="py-2 text-right text-gray-900 dark:text-white">
                                  {usages.auditor_licenses_max != null
                                    ? Math.max(0, usages.auditor_licenses_max - (usages.auditor_licenses_used ?? 0))
                                    : '—'}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                      {subscription.subscription.licensed_until && (
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.licenseValidUntil')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                            <input
                              type="text"
                              value={(() => {
                                const date = new Date(subscription.subscription.licensed_until as string);
                                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                              })()}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                            />
                          </dd>
                        </div>
                      )}
                      <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.maxAllowedDevices')}</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                          <input
                            type="number"
                            value={subscription.subscription.max_allowed_devices_per_user ?? ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                          />
                        </dd>
                      </div>
                      <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('company.maxFoldersPerDevice')}</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                          <input
                            type="number"
                            value={subscription.subscription.max_folders_per_device ?? ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                          />
                        </dd>
                      </div>
                    </>
                  )}
                  
                  {/* Edit Actions */}
                  {editingCompany && (
                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 border-t border-gray-200 dark:border-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400"></dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2">
                        <div className="flex space-x-3">
                          <button
                            onClick={handleSaveCompany}
                            disabled={savingCompany}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-governate-600 text-white hover:bg-governate-700 transition-colors disabled:opacity-50"
                          >
                            {savingCompany ? t('company.saving') : t('company.saveChanges')}
                          </button>
                          <button
                            onClick={() => {
                              setEditingCompany(false);
                              // Reset form data to original company data
                              if (company) {
                                setCompanyFormData({
                                  name: company.name || '',
                                  domain: company.domain || '',
                                  description: company.description || '',
                                  website: company.website || '',
                                  logo_url: company.logo_url || '',
                                  app_version: company.app_version || 'latest',
                                  contact_person: company.contact_person || '',
                                  contact_person_email: company.contact_person_email || '',
                                  contact_email: company.contact_email || '',
                                  address: company.address || '',
                                  contract_id: company.contract_id || '',
                                });
                              }
                            }}
                            disabled={savingCompany}
                            className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                          >
                            {t('company.cancel')}
                          </button>
                        </div>
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {user?.company_id ? t('company.unableToLoad') : t('company.notAssociatedWithCompany')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'policies' && (
        <PolicyTab />
      )}

      {activeTab === 'watermark' && (
        <WatermarkTab />
      )}

      {activeTab === 'notifications' && (
        <NotificationsTab />
      )}

      {activeTab === 'security' && (
        <SecurityTab />
      )}
    </div>
  );
};

export default Settings;
